// ============================================================
// TargetingSilhouette - the targeting console's art and its zones
// ============================================================
// A horde draws as one of what it is made of, dimmed (hits strike the
// mass); any other body draws as its plate thumbnail, the selected zone
// lit, and - when a called shot is possible - a clickable targeting node
// per zone.

import type { BodyConfig } from '../../engine/bodyConfig';
import type { BodyTypeId, Target } from '../../types';
import SightRing from '../SightRing';
import clsx from '../../utils/clsx';
import { NOBODY, type PlateFigure } from '../../engine/plate/figureFor';
import PlateThumb from '../plate/PlateThumb';
import styles from '../../styles/TargetingConsole.module.css';

const STEEL = '#d9dde2';

interface Props {
  target: Target | null;
  bodyType: BodyTypeId;
  isHorde: boolean;
  config: BodyConfig;
  /** 'auto' or a location id of THIS body. */
  selected: string;
  interactive: boolean;
  penalty: number;
  /** The armour readout for a zone. */
  armourFor: (locId: string) => string;
  onEngageZone: (locId: string) => void;
  onClear: () => void;
  /** The target's plate figure (plan body-map P4, P8); null with no target, drawn as a plain human. */
  body: PlateFigure | null;
}

/** The nodes' scale on a plate thumbnail (SightRing draws r = 22 x this, in the figure's 2123 x 3000 frame). */
const THUMB_SF = 10;

export function TargetingSilhouette(props: Props) {
  if (props.isHorde) {
    // One of what the mass is made of, dimmed: hits strike the mass, not a location; a crowd at this size is specks.
    return (
      <PlateThumb
        body={props.body ?? NOBODY}
        colour={STEEL}
        ariaLabel="Mass target"
        className={clsx(styles.svg, styles.dimmed)}
      />
    );
  }
  return (
    <PlateThumb
      body={props.body ?? NOBODY}
      colour={STEEL}
      ariaLabel={`${props.config.label} targeting silhouette`}
      className={clsx(styles.svg, !props.interactive && styles.dimmed)}
      selected={props.selected}
      zone={props.interactive ? (id, [cx, cy]) => <PlateNode key={id} id={id} cx={cx} cy={cy} {...props} /> : undefined}
    />
  );
}

/** A plate location's targeting node at its anchor, where the body names that location. */
function PlateNode({ id, cx, cy, ...props }: Props & { id: string; cx: number; cy: number }) {
  const loc = props.config.locations.find((l) => l.id === id);
  if (!loc) return null;
  return <TargetZone {...props} part={{ id, tx: cx, ty: cy }} name={loc.name} sf={THUMB_SF} />;
}

interface TargetZoneProps extends Props {
  /** Where the node sits: the location's anchor on the plate. */
  part: { id: string; tx: number; ty: number };
  name: string;
  sf: number;
}

/**
 * One zone's targeting node. Enter and click only: Space deliberately does
 * nothing, because the window keydown listener would ALSO fire the attack.
 */
function TargetZone({ part: p, name, sf, selected, penalty, armourFor, onEngageZone, onClear }: TargetZoneProps) {
  const engaged = selected === p.id;
  const press = () => (engaged ? onClear() : onEngageZone(p.id));
  return (
    <g
      role="button"
      tabIndex={0}
      data-testid={`target-zone-${p.id}`}
      data-sight-zone
      aria-label={engaged ? `${name} - clear called shot` : `${name} - called shot ${-penalty || 0}`}
      aria-pressed={engaged}
      className={styles.zone}
      onClick={press}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        press();
      }}
    >
      <title>{`${name} - ${armourFor(p.id)}`}</title>
      <SightRing cx={p.tx} cy={p.ty} r={22 * sf} sf={sf} engaged={engaged} />
    </g>
  );
}
